import React, {
  useEffect,
  useState,
  useRef,
  SetStateAction,
  Dispatch
} from 'react'
import { DeleteOutlined } from '@ant-design/icons'
import { InvalidEmail, User } from './types'
import { Row, Col, Button, Pagination, Modal, Tabs, Typography } from 'antd'
import UploadCSV from './BulkAddDrawer/UploadCSV'

const { TabPane } = Tabs
const { Text } = Typography

interface ExclusiveFormProps {
  setCustomerIds: Dispatch<SetStateAction<string[] | []>>
  customers: User[] | undefined
}

const ExclusiveForm = (props: ExclusiveFormProps) => {
  const { setCustomerIds, customers = [] } = props
  const [fileName, setFileName] = useState<string>('')
  const [showEmails, setShowEmails] = useState<boolean>(false)
  const [hasFile, setHasFile] = useState<boolean>(false)
  const [activeKey, setActiveKey] = useState<string>('1')
  const [validEmails, setValidEmails] = useState<User[]>([])
  const [customerPage, setCustomerPage] = useState<User[]>([])
  const [customerList, setCustomerList] = useState<User[]>(customers)
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [invalidEmails, setInvalidEmails] = useState<InvalidEmail>({
    message: '',
    list: []
  })
  const uploadRef = useRef<HTMLInputElement>(null)

  const onPageChange = (page: number, pageSize: number) => {
    setPage(page)
    setPageSize(pageSize)
    const start = page === 1 ? 0 : page * pageSize - pageSize
    const end = page * pageSize

    const newPage = customerList
      .sort((a, b) => a.first_name.localeCompare(b.first_name))
      .slice(start, end)
    setCustomerPage(newPage)
  }

  const handleDelete = (email: string) => {
    setCustomerList(customerList.filter((user) => user.email !== email))
    setValidEmails(validEmails.filter((user) => user.email !== email))
    onPageChange(page, pageSize)
  }

  useEffect(() => {
    if (!!validEmails.length) {
      onPageChange(page, pageSize)

      const customerListId = customerList.map((c) => c.id) || []
      const newCustomerList = [
        ...customerList,
        ...validEmails.filter((v) => !customerListId.includes(v.id))
      ]
      setCustomerList(newCustomerList)
    }
  }, [validEmails])

  useEffect(() => {
    onPageChange(page, pageSize)
    setCustomerIds([...(customerList.map((v) => v?.id) || [])])
  }, [customerList])

  return (
    <>
      <UploadCSV
        invalidEmails={invalidEmails}
        setInvalidEmails={setInvalidEmails}
        validEmails={validEmails}
        setValidEmails={setValidEmails}
        hasFile={hasFile}
        setHasFile={setHasFile}
        fileName={fileName}
        setFileName={setFileName}
        setShowEmails={setShowEmails}
        uploadRef={uploadRef}
        exclusive={true}
      />

      {!!customerList.length && (
        <>
          <Row>
            <Text strong>Customers ({customerList.length})</Text>
          </Row>
          {customerPage.map(({ first_name, last_name, email }: User, index) => (
            <Row key={index} gutter={8} align={'middle'} className='_mb-8'>
              <Col span={8}>
                <div className='user-detail'>
                  {first_name} {last_name}
                </div>
              </Col>
              <Col span={8}>
                <div className='user-detail'>{email}</div>
              </Col>
              <Col span={8}>
                <Button
                  className='delete'
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(email)}
                />
              </Col>
            </Row>
          ))}
          <Row justify={'center'}>
            <Pagination
              onChange={onPageChange}
              defaultCurrent={1}
              total={validEmails.length}
            />
          </Row>
        </>
      )}

      <Modal
        visible={showEmails}
        cancelText='close'
        cancelButtonProps={{ type: 'ghost', className: '-secondary' }}
        okText='upload other file'
        okButtonProps={{ type: 'default' }}
        title='Email addresses'
        onCancel={() => setShowEmails(false)}
        onOk={() => uploadRef.current?.click()}
      >
        <Tabs
          className='bulk-rewards-tabs'
          activeKey={activeKey}
          onChange={(key: string) => setActiveKey(key)}
        >
          <TabPane
            key='1'
            tab={`Match (${validEmails.length})`}
            disabled={!validEmails.length}
          >
            {Boolean(validEmails.length) && (
              <Row>
                {validEmails.map(({ email }: User) => (
                  <Col
                    span='11'
                    className='email-list _ml-10 _mb-10'
                    key={email}
                  >
                    {email}
                  </Col>
                ))}
              </Row>
            )}
          </TabPane>
          <TabPane
            key='2'
            tab={`No Match (${invalidEmails.list.length})`}
            disabled={!invalidEmails.list.length}
          >
            {Boolean(invalidEmails.list.length) && (
              <Row>
                {invalidEmails.list.map((email: string) => (
                  <Col
                    span='11'
                    className='email-list _ml-10 _mb-10'
                    key={email}
                  >
                    {email}
                  </Col>
                ))}
              </Row>
            )}
          </TabPane>
        </Tabs>
      </Modal>
    </>
  )
}

export default ExclusiveForm
